import moment from 'moment';
import uuid from 'uuid/v4';
import api from '@/api';
import { getFullName, getPlatformType, setQuerySilent } from '@/lib/utils';
import { CONFIGURATOR_UPDATE_QUANTITY } from '@/apps/configurator/store/types';
import { FriesMenu } from '@/apps/menu/constants';
import { MENU_LOAD, LAZY_MENU_LOAD } from '@/apps/menu/store/types';
import {
  CART_DISPLAY,
  CART_ITEM_REMOVE,
  CART_ITEM_SET_QUANTITY,
  CART_RESET,
  CART_ITEM_ADD,
  CART_ITEM_UPDATE,
  ORDER_PAY,
  ORDER_SET_RESTAURANT,
  ORDER_SET_TYPE,
  ORDER_CREATE,
  CART_EXPAND,
  CART_COLLAPSE,
  ORDER_SAVE,
  ORDERS_LOAD,
  ORDER_LOAD,
  CART_SAVE_ORDER,
  CART_PREPARE_ORDER,
  CART_CLEAR_USER_ORDERS,
  ORDER_PATCH,
  ORDER_SET_TERMINAL,
  TERMINAL_CONNECTION_TOKEN,
  CAPTURE_ORDER,
  ORDER_RESET,
  ORDER_UPDATE_ID,
  PAYMENT_INTENT,
  SHOW_CHECKOUT_DIALOG,
  HIDE_CHECKOUT_DIALOG,
  ORDER_CALC,
  CART_CALC_ORDER,
  UPDATE_DISCOUNT,
  SET_DISCOUNT_CODE_UPDATING,
  SET_DISCOUNT_ERRORS,
  DISCOUNT_CODE_CHANGED,
} from './types';

export default {
  /**
   * Toggle cart navigation drawer
   * @param commit
   * @param {boolean} value - Defines display for cart
   * @returns {Promise<void>}
   */
  [CART_DISPLAY]: async ({ commit }, value) => {
    commit(CART_DISPLAY, value);
  },
  /**
   * Create new order item uuid and place it cart
   * @param commit
   * @param productKey
   */
  [CART_ITEM_ADD]: ({ commit }, { productKey, orderItem = {} }) => {
    const orderItemId = uuid();

    commit(CART_ITEM_UPDATE, {
      orderItemId,
      orderItem: { data: orderItem, productKey, quantity: 1 },
    });

    return orderItemId;
  },
  /**
   * Update order item in cart
   * @param commit
   * @param orderItemId
   * @param orderItem
   */
  [CART_ITEM_UPDATE]: ({ commit }, { orderItemId, orderItem }) => {
    commit(CART_ITEM_UPDATE, { orderItemId, orderItem });
  },

  /**
   * Remove item from the cart by ID
   * @prop itemId - id of item in cart
   */
  [CART_ITEM_REMOVE]: async ({ commit }, itemId) => {
    commit(CART_ITEM_REMOVE, itemId);
  },

  /**
   * Remove item from the cart by ID
   * @prop itemId - id of item in cart
   * @prop quantity - new quantity of item
   */
  [CART_ITEM_SET_QUANTITY]: async ({ commit, dispatch }, {
    itemId,
    quantity,
  }) => {
    commit(CART_ITEM_SET_QUANTITY, { itemId, quantity });
    dispatch(CONFIGURATOR_UPDATE_QUANTITY, { orderItemId: itemId, quantity });
  },

  /**
   * Remove all items from cart
   */
  [CART_RESET]: async ({ commit }) => {
    commit(CART_RESET);
  },

  /**
   * Remove order data.
   */
  [ORDER_RESET]: async ({ commit }) => {
    commit(ORDER_RESET);
  },

  /**
   * Pay the order
   * @param {String} orderId - order identifier
   * @param {String} stripeToken - stripe card token
   * @returns {Promise}
   */
  /* eslint-disable no-unused-vars */
  [ORDER_PAY]: async ({ commit, getters }, { methodId, intentId }) => {
    const orderId = getters.order.id;
    /* eslint-enable no-unused-vars */
    const resp = await api.cart.orderPay({
      orderId,
      methodId,
      intentId,
    });

    return resp.data;
  },

  /**
   * Get payment intent from backend
   * @param {String} orderId - order identifier
   * @param {String} stripeToken - stripe card token
   * @returns {Promise}
   */
  /* eslint-disable no-unused-vars */
  [PAYMENT_INTENT]: async ({ commit, getters }) => {
    const orderId = getters.order.id;
    /* eslint-enable no-unused-vars */
    const resp = await api.cart.paymentIntent({
      orderId,
    });

    return resp.data;
  },


  /**
   * Capture the terminal order
   * @param {String} orderId - order identifier
   * @param {String} intentId - stripe intent id
   * @returns {Promise}
   */
  /* eslint-disable no-unused-vars */
  [CAPTURE_ORDER]: async ({ commit, getters }, { intentId }) => {
    const orderId = getters.order.id;
    /* eslint-enable no-unused-vars */
    const resp = await api.cart.capture({
      orderId,
      intentId,
    });

    return resp.data;
  },

  /**
   * Get terminal connection token
   * @returns {Promise}
   */
  /* eslint-disable no-unused-vars */
  [TERMINAL_CONNECTION_TOKEN]: async ({ commit }) => {
    /* eslint-enable no-unused-vars */
    const resp = await api.cart.connectToken();

    return resp.data.secret;
  },

  /**
   * Set selected order type
   * @param {String} orderType - one from `ORDER_TYPES`
   */
  [ORDER_SET_TYPE]: async ({ commit }, orderType) => {
    commit(ORDER_SET_TYPE, orderType);
  },

  /**
   * Set selected rest
   * @param {Number} restaurantId
   */
  [ORDER_SET_RESTAURANT]: async ({ commit, dispatch }, restaurantId) => {
    commit(ORDER_SET_RESTAURANT, restaurantId);
    if (restaurantId != null) {
      await Promise.all([
        dispatch(MENU_LOAD, { restaurantId }),
        dispatch(LAZY_MENU_LOAD, { restaurantId }),
      ]);
    }
  },

  /**
   * Set selected terminal id
   * @param {Number} restId
   */
  [ORDER_SET_TERMINAL]: async ({ commit }, terminalId) => {
    commit(ORDER_SET_TERMINAL,  terminalId);
  },

  /**
   * Create new order on backend
   * @param commit
   * @param orderData
   * @returns {Promise<void>}
   */
  [ORDER_CREATE]: async ({ commit }, orderData) => {
    const resp = await api.cart.orderCreate(orderData);

    commit(ORDER_CREATE, resp.data);

    return resp;
  },

  /**
   * Create new order on backend
   * @param commit
   * @param orderData
   * @returns {Promise<void>}
   */
  [ORDER_CALC]: async ({ commit, state }, orderData) => {
    try {
      state.orderUpdated = true;

      const resp = await api.cart.orderCalc(orderData);

      commit(ORDER_CALC, resp.data);

      return resp;
    } finally {
      state.orderUpdated = false;
    }
  },

  /**
   * Cart expand.
   */
  [CART_EXPAND]: ({ commit }) => {
    setQuerySilent({ cart: true  });
    commit(CART_DISPLAY, true);
  },

  /**
   * Cart collapse.
   * @param commit
   * @param { boolean } openOnReturn - If you need to open when returning back to the route.
   */
  [CART_COLLAPSE]: ({ commit }, openOnReturn = false) => {
    setQuerySilent({ cart: openOnReturn  });
    commit(CART_DISPLAY, false);
  },

  /**
   * Save already created order to cart.
   */
  [ORDER_SAVE]: async  ({ commit }, { orderItem }) => {
    const orderItemId = uuid();

    commit(CART_ITEM_UPDATE, { orderItemId, orderItem });
  },
  /**
   * Load already created orders.
   */
  [ORDERS_LOAD]: async ({ commit, getters }) => {
    const resp = await api.cart.orders();

    resp.data.forEach((order) => {
      let orderTitle = '';
      const products = order.items.map((product, index) => {
        const productFromMenu = getters.productByKey(product.menuProductId);
        const nameParts = productFromMenu.name.split(' ');

        orderTitle += `${product.quantity} `;
        nameParts.forEach((namePart, partIndex) => {
          orderTitle += `${namePart.substr(0, 1).toUpperCase()}${namePart.substr(1)}`;
          if (partIndex !== nameParts.length - 1) {
            orderTitle += ' ';
          }
        });

        if (index !== order.items.length - 1) {
          orderTitle += ', ';
        }
        // need to change image  of BYF from ProductImages/BYF.png to ProductImages/fries.png' because BYF.png
        // doesnt's suit for orders

        // eslint-disable-next-line max-len
        if (product.menuProductId === parseInt(FriesMenu.PRODUCT_ID, 10) && productFromMenu.image !== FriesMenu.BOSS_YOUR_FRIES_ORDER_PICTURE) {
          productFromMenu.image = FriesMenu.BOSS_YOUR_FRIES_ORDER_PICTURE;
        }

        return {
          productId: product.menuProductId,
          quantity:  product.quantity,
          items:     product.composition,
          image:     productFromMenu.image,
        };
      });

      const transformedOrder = {
        orderId: order.id,
        date:    order.created,
        price:   order.totalPrice,
        title:   orderTitle,
        image:   products.length !== 0 ? products[0].image : '',
        products,
      };

      if (!getters.orders
        .find(order => order.orderId === transformedOrder.orderId)) {
        commit(ORDER_LOAD, transformedOrder);
      }
    });
  },
  [CART_SAVE_ORDER]: async ({ commit, getters }, order) => {
    order.products.forEach((product) => {
      const data = {};

      product.items.forEach((item) => {
        const { categoryKey, name } = getters.itemByKey(item.menuItemId);
        const newItem = {};

        newItem[item.menuItemId] = {
          name,
          modifier: item.modifier,
          quantity: item.quantity,
        };
        if (data[categoryKey]) {
          data[categoryKey] = Object.assign({}, newItem, data[categoryKey]);
        } else {
          data[categoryKey] = newItem;
        }
      });

      commit(CART_ITEM_UPDATE, {
        orderItemId: uuid(),
        orderItem:   {
          productKey: product.productId,
          quantity:   product.quantity,
          data,
        },
      });
    });
  },

  /**
   * Prepare order.
   * @param userData
   * @param promoCode
   * @returns {Promise<void>}
   */
  [CART_PREPARE_ORDER]: async ({ getters, dispatch }, {
    userData,
    promoCode,
  } = {}) => {
    const tempUserData = userData || getters.authUser || getters.guestAccountData;

    const order = {
      restaurant_id:  getters.restaurantId,
      pickup_type:    getters.orderType,
      recipient_name: getFullName(tempUserData),
      first_name:     tempUserData.firstName,
      last_name:      tempUserData.lastName,
      phone:          tempUserData.phone,
      email:          tempUserData.email,
      device_type:    getPlatformType(),
      items:          getters.getPreparedItems,
      promotion_code: promoCode,
    };

    return dispatch(ORDER_CREATE, order);
  },

  [CART_CALC_ORDER]: ({ getters, dispatch }, {
    userData,
    promoCode,
  } = {}) => {
    const tempUserData =
      userData ||
      getters.authUser ||
      getters.getNotAuthUserData;

    const order = {
      recipient_name: getFullName(tempUserData),
      first_name:     tempUserData.firstName,
      last_name:      tempUserData.lastName,
      phone:          tempUserData.phone,
      email:          tempUserData.email,
      restaurant_id:  getters.restaurantId,
      pickup_type:    getters.orderType,
      device_type:    getPlatformType(),
      items:          getters.getPreparedItems,
      promotion_code: promoCode,
    };

    return dispatch(ORDER_CALC, order);
  },

  /**
   * Clear user orders.
   */
  [CART_CLEAR_USER_ORDERS]: ({ commit }) => {
    commit(CART_CLEAR_USER_ORDERS);
  },
  [ORDER_PATCH]: async ({ commit, getters }, orderUpdate) => {
    const orderId = getters.order.id;

    const resp = await api.cart.orderPatch(orderId, orderUpdate);

    /** To display local time on site, send to backend - utc */
    if (moment.isMoment(orderUpdate.pickup_time)) {
      resp.data.pickupTime = orderUpdate.pickup_time.format();
    }

    commit(ORDER_CREATE, resp.data);

    return resp;
  },
  [ORDER_UPDATE_ID]: async ({ commit, getters }) => {
    const orderData = getters.order;

    const resp = await api.cart.order(orderData.id);

    orderData.externalId = resp.data.externalId;

    commit(ORDER_CREATE, orderData);

    return resp;
  },

  /**
   * Show checkout dialog.
   * @param commit
   */
  [SHOW_CHECKOUT_DIALOG]: ({ commit }) => {
    setQuerySilent({ loginSuggest: true  });
    commit(SHOW_CHECKOUT_DIALOG);
  },

  /**
   * Hide checkout dialog.
   * @param commit
   * @param { boolean } openOnReturn - If you need to open when returning back to the route.
   */
  [HIDE_CHECKOUT_DIALOG]: ({ commit }, openOnReturn = false) => {
    setQuerySilent({ loginSuggest: openOnReturn  });
    commit(HIDE_CHECKOUT_DIALOG);
  },

  /**
   * Updates order with a new discount code.
   */
  [UPDATE_DISCOUNT]: async ({ commit, getters, dispatch }) => {
    const { discountCode } = getters;

    if (getters.discountCodeIsUpdating) {
      return;
    }

    try {
      commit(SET_DISCOUNT_CODE_UPDATING, true);

      await dispatch(ORDER_PATCH, { promotion_code: discountCode });

      commit(SET_DISCOUNT_ERRORS, []);
    } catch (error) {
      if (error.response && error.response.data) {
        const { data } = error.response;

        if (data.data.promotion_code) {
          commit(SET_DISCOUNT_ERRORS, data.data.promotion_code);
        }
      }
      throw error;
    } finally {
      commit(SET_DISCOUNT_CODE_UPDATING, false);
    }
  },

  [DISCOUNT_CODE_CHANGED]: ({ commit }, discountCode) => {
    commit(DISCOUNT_CODE_CHANGED, discountCode);
  },
};



// WEBPACK FOOTER //
// ./src/apps/cart/store/actions.js